import { combineReducers, createReducer, on } from '@ngrx/store';
import * as Actions from './app.actions';
import * as fromConfigEditorScreen from './config-editor-screen/config-editor-screen.reducer';
import * as fromContentGroupEditScreen from './content-group-edit-screen/content-group-edit-screen.reducer';
import * as fromContentGroupListScreen from './content-group-list-screen/content-group-list-screen.reducer';
import * as fromContentGroupScreen from './content-group/content-group.reducer';
import * as fromCreateTrayScreen from './create-tray-screen/create-tray-screen.reducer';
import * as fromGeneralConfigEditorScreen from './tenant-independent/general-config-editor-screen/general-config-editor-screen.reducer';
import * as fromGeneralStreamingConfigEditorScreen from './tenant-independent/general-streaming-config-editor-screen/general-streaming-config-editor-screen.reducer';
import { Country } from './domain/country';
import { Platform } from './domain/platform';
import { UserSegment } from './domain/user-segment';
import * as fromFileUpload from './file-upload/file-upload.reducer';
import * as fromMenuEditor from './menu-editor/menu-editor.reducer';
import * as fromPageEditScreen from './page-edit-screen/page-edit-screen.reducer';
import * as fromPageScreen from './page-screen/page-screen.reducer';
import * as fromApplicationListAndMetadataScreen from './application-list-and-metadata/application-list-and-metadata.reducer';
import * as fromChannelListScreen from './channel-list/channel-list.reducer';
import * as fromBrandSettingsScreen from './brand-settings/brand-settings.reducer';
import * as fromRedirectPolicies from './redirect-policies/redirect-policies.reducer';
import * as tenantIndependentContentGroupListScreen from './tenant-independent-content-group/tenant-independent-content-group.reducer';
import * as tenantIndependentContentGroupEditScreen from './tenant-independent-content-group/components/tenant-independent-content-group-edit-screen/tenant-independent-content-group-edit-screen.reducer';
import { CountryGroup } from './domain/country-group';
import { Tenant } from './domain/tenant';
import { Genre } from './domain/genre';
import { Feed } from './domain/feed';
import { AdminUiDetails } from './domain/admin-ui-details';
import { TrayCategoriesApiModel } from '@cxb/editor-stb-api';

export const featureKey = 'cxbApp';
export const featureAppKey = 'app';
export const combinedFeatureKey = 'cxbCombinedApp';

export interface AppState {
  userSegments: UserSegment[];
  countries: Country[];
  countryGroups: CountryGroup[];
  platforms: Platform[];
  genres: Genre[];
  feeds: Feed[];
  loading: boolean;
  rootLevelRoles: string[] | null;
  tenantOptions: Tenant[] | null;
  selectedTenant: Tenant | null;
  adminUIDetails: AdminUiDetails | null;
  trayOptions: TrayCategoriesApiModel | null;
  apiVersion: string;
}

export interface CombinedAppState {
  [fromMenuEditor.featureKey]: fromMenuEditor.FeatureState;
  [fromPageEditScreen.featureKey]: fromPageEditScreen.FeatureState;
  [fromCreateTrayScreen.featureKey]: fromCreateTrayScreen.FeatureState;
  [fromFileUpload.featureKey]: fromFileUpload.FeatureState;
  [fromContentGroupScreen.featureKey]: fromContentGroupScreen.FeatureState;
  [fromContentGroupListScreen.featureKey]: fromContentGroupListScreen.FeatureState;
  [fromContentGroupEditScreen.featureKey]: fromContentGroupEditScreen.FeatureState;
  [fromConfigEditorScreen.featureKey]: fromConfigEditorScreen.FeatureState;
  [fromPageScreen.featureKey]: fromPageScreen.FeatureState;
  [fromApplicationListAndMetadataScreen.featureKey]: fromApplicationListAndMetadataScreen.FeatureState;
  [fromChannelListScreen.featureKey]: fromChannelListScreen.FeatureState;
  [fromBrandSettingsScreen.featureKey]: fromBrandSettingsScreen.FeatureState;
  [fromRedirectPolicies.featureKey]: fromRedirectPolicies.FeatureState;
  [tenantIndependentContentGroupListScreen.featureKey]: tenantIndependentContentGroupListScreen.FeatureState;
  [tenantIndependentContentGroupEditScreen.featureKey]: tenantIndependentContentGroupEditScreen.FeatureState;
}

export interface RootAppState {
  app: AppState;
}

export const initialState: AppState = {
  // TODO:CXBMERGE:AB Temporary dummy platform to make the app work
  platforms: [],
  userSegments: [],
  countries: [],
  countryGroups: [],
  genres: [],
  feeds: [],
  loading: true,
  rootLevelRoles: null,
  tenantOptions: null,
  selectedTenant: null,
  adminUIDetails: null,
  trayOptions: null,
  apiVersion: '',
};

export const appReducer = createReducer(
  initialState,
  on(Actions.setCxbIsLoading, (state, action): AppState => ({ ...state, loading: action.loading })),
  on(Actions.startLoading, (state): AppState => ({ ...state, loading: true })),
  on(Actions.finishLoading, (state): AppState => ({ ...state, loading: false })),
  on(Actions.loadCxbUserSegments, (state): AppState => ({ ...state, loading: true })),
  on(
    Actions.loadCxbUserSegmentsSuccess,
    (state, action): AppState => ({
      ...state,
      loading: false,
      userSegments: action.userSegments,
    })
  ),
  on(Actions.loadCxbUserSegmentsFail, (state): AppState => ({ ...state, loading: false })),

  on(Actions.loadCxbPlatforms, (state): AppState => ({ ...state, loading: true })),
  on(Actions.loadCxbPlatformsSuccess, (state, action): AppState => ({ ...state, loading: false, platforms: action.platforms })),
  on(Actions.loadCxbPlatformsFail, (state): AppState => ({ ...state, loading: false })),

  on(Actions.loadCxbCountries, (state): AppState => ({ ...state, loading: true })),
  on(
    Actions.loadCxbCountriesSuccess,
    (state, action): AppState => ({
      ...state,
      loading: false,
      countries: action.countries,
      countryGroups: action.countryGroups,
    })
  ),
  on(Actions.loadCxbCountriesFail, (state): AppState => ({ ...state, loading: false })),

  on(Actions.fetchGenres, (state): AppState => ({ ...state, loading: true })),
  on(Actions.fetchGenresSuccess, (state, action): AppState => ({ ...state, loading: false, genres: action.genres })),
  on(Actions.fetchGenresFail, (state): AppState => ({ ...state, loading: false })),

  ...Actions.fetchCxbFeeds.reduce<AppState>((state, action) => ({ ...state, feeds: action.feeds })),

  // LOADING USER DATA
  on(Actions.loadCxbUserData, (state): AppState => ({ ...state, loading: true })),
  on(
    Actions.loadCxbUserDataSuccess,
    (state, action): AppState => ({
      ...state,
      loading: false,
      rootLevelRoles: action.rootLevelRoles,
      tenantOptions: action.availableTenants || [],
    })
  ),
  on(Actions.loadCxbUserDataFail, (state): AppState => ({ ...state, loading: false })),

  // SELECTING TENANT
  on(Actions.saveCxbUserSelectedTenant, (state): AppState => ({ ...state, loading: true })),
  on(
    Actions.saveCxbUserSelectedTenantSuccess,
    (state, action): AppState => ({
      ...state,
      selectedTenant: action.tenant,
      loading: false,
    })
  ),
  on(
    Actions.saveCxbUserSelectedTenantFail,
    (state, action): AppState => ({
      ...state,
      loading: false,
    })
  ),

  // LOADING TENANT
  on(
    Actions.loadCxbUserSelectedTenant,
    (state, action): AppState => ({
      ...state,
      loading: true,
    })
  ),
  on(
    Actions.loadCxbUserSelectedTenantSuccess,
    (state, action): AppState => ({
      ...state,
      loading: false,
      selectedTenant: action.tenant,
    })
  ),
  on(
    Actions.loadCxbUserSelectedTenantFail,
    (state, action): AppState => ({
      ...state,
      loading: false,
    })
  ),

  // SWITCHING TENANT
  on(
    Actions.switchCxbTenant,
    (state, action): AppState => ({
      ...state,
      selectedTenant: null,
    })
  ),

  // admin ui details
  on(Actions.fetchAdminUIDetails, (state): AppState => ({ ...state, loading: true })),
  on(
    Actions.fetchAdminUIDetailsSuccess,
    (state, action): AppState => ({ ...state, loading: false, adminUIDetails: action.adminUIDetails })
  ),
  on(Actions.fetchAdminUIDetailsFail, (state): AppState => ({ ...state, loading: false })),

  on(Actions.fetchTrayOptions, (state): AppState => ({ ...state, loading: true })),
  on(Actions.fetchTrayOptionsSuccess, (state, action): AppState => ({ ...state, loading: false, trayOptions: action.trayOptions })),
  on(Actions.fetchTrayOptionsFail, (state): AppState => ({ ...state, loading: false })),

  on(Actions.fetchApiVersionSuccess, (state, action): AppState => ({ ...state, apiVersion: action.version }))
);

export const combinedAppReducer = combineReducers({
  [fromMenuEditor.featureKey]: fromMenuEditor.reducer,
  [fromPageEditScreen.featureKey]: fromPageEditScreen.reducer,
  [fromCreateTrayScreen.featureKey]: fromCreateTrayScreen.reducer,
  [fromFileUpload.featureKey]: fromFileUpload.reducer,
  [fromContentGroupScreen.featureKey]: fromContentGroupScreen.reducer,
  [fromContentGroupListScreen.featureKey]: fromContentGroupListScreen.reducer,
  [fromContentGroupEditScreen.featureKey]: fromContentGroupEditScreen.reducer,
  [fromConfigEditorScreen.featureKey]: fromConfigEditorScreen.reducer,
  [fromPageScreen.featureKey]: fromPageScreen.reducer,
  [fromGeneralConfigEditorScreen.featureKey]: fromGeneralConfigEditorScreen.reducer,
  [fromApplicationListAndMetadataScreen.featureKey]: fromApplicationListAndMetadataScreen.reducer,
  [fromChannelListScreen.featureKey]: fromChannelListScreen.reducer,
  [fromBrandSettingsScreen.featureKey]: fromBrandSettingsScreen.reducer,
  [fromGeneralStreamingConfigEditorScreen.featureKey]: fromGeneralStreamingConfigEditorScreen.reducer,
  [fromRedirectPolicies.featureKey]: fromRedirectPolicies.reducer,
  [tenantIndependentContentGroupListScreen.featureKey]: tenantIndependentContentGroupListScreen.reducer,
  [tenantIndependentContentGroupEditScreen.featureKey]: tenantIndependentContentGroupEditScreen.reducer,
});
